import React, { useMemo, useState } from 'react';
import { Badge, Button, Col, Form, Row } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import LookupLayout from '../layout/LookupLayout';
import {
  buildContextPayload,
  contextGrantChipLabel,
  dedupeGrants,
  getContextGrantChipBadgeProps,
  grantLookupMeta,
  normalizeContextTargetType,
  parseContextGrants,
} from '../../utils/grantContextUtils';

/**
 * Multi-select USER / ROLE / TEAM grants (same shape as validation rule_context).
 */
const GrantContextPicker = ({
  value = null,
  onChange,
  disabled = false,
  emptyHint = 'None — visible to everyone.',
  idPrefix = 'grant-context',
}) => {
  const grants = useMemo(() => parseContextGrants(value), [value]);
  const [pickerType, setPickerType] = useState('USER');
  const [lookupNonce, setLookupNonce] = useState(0);

  const pickerLookup = grantLookupMeta(pickerType);

  const emitGrants = (nextGrants) => {
    onChange?.(buildContextPayload(nextGrants));
  };

  const onPickerSelect = (id, label) => {
    if (!id || disabled) return;
    const next = dedupeGrants([
      ...grants,
      {
        target_type: pickerType,
        target_id: String(id),
        target_label: label != null ? String(label) : '',
      },
    ]);
    emitGrants(next);
    setLookupNonce((n) => n + 1);
  };

  const removeGrant = (idx) => {
    if (disabled) return;
    const next = grants.filter((_, i) => i !== idx);
    emitGrants(next);
  };

  return (
    <div>
      <Row className="g-2 align-items-end mb-2">
        <Col md={3}>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-1">Type</Form.Label>
            <Form.Select
              size="sm"
              value={pickerType}
              onChange={(e) => {
                setPickerType(e.target.value);
                setLookupNonce((n) => n + 1);
              }}
              disabled={disabled}
            >
              <option value="USER">User</option>
              <option value="ROLE">Role</option>
              <option value="TEAM">User group</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={9}>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-1">Add</Form.Label>
            <LookupLayout
              key={`${idPrefix}-${pickerType}-${lookupNonce}`}
              rootClassName="grant-context-lookup"
              objectName={pickerLookup.objectName}
              searchFields={pickerLookup.searchFields}
              displayFields={pickerLookup.displayFields}
              value=""
              defaultLabel=""
              onChange={onPickerSelect}
              placeholder={pickerLookup.placeholder}
              disabled={disabled}
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-0">
        <Form.Label className="d-block small mb-1 fw-semibold">Selected</Form.Label>
        <div className="rounded border p-1 px-2 bg-light" style={{ minHeight: 36 }}>
          <div className="d-block" style={{ lineHeight: 1.45 }}>
            {grants.map((g, idx) => {
              const chip = getContextGrantChipBadgeProps(g.target_type);
              const t = normalizeContextTargetType(g.target_type);
              const closeBtnClass =
                t === 'TEAM'
                  ? 'p-0 d-inline-flex align-items-center justify-content-center text-dark'
                  : 'p-0 d-inline-flex align-items-center justify-content-center text-white text-opacity-75';
              return (
                <Badge
                  key={`${g.target_type}-${g.target_id}-${idx}`}
                  bg={chip.bg}
                  text={chip.text}
                  className={`${chip.className} me-1 mb-1 align-middle`}
                >
                  <span className="me-1 text-break">{contextGrantChipLabel(g)}</span>
                  <Button
                    type="button"
                    variant="link"
                    className={closeBtnClass}
                    size="sm"
                    style={{ minWidth: 22, minHeight: 22 }}
                    onClick={() => removeGrant(idx)}
                    disabled={disabled}
                    aria-label="Remove grant"
                  >
                    <FaTimes className="small" style={{ fontSize: '0.65rem' }} />
                  </Button>
                </Badge>
              );
            })}
          </div>
          {grants.length === 0 && (
            <p className="text-muted small mb-0" style={{ fontSize: '0.8rem' }}>
              {emptyHint}
            </p>
          )}
        </div>
      </Form.Group>
    </div>
  );
};

export default GrantContextPicker;
